import DOMPurify from 'dompurify'
import sanitizeHtml from 'sanitize-html'
import jwtDecode from 'jwt-decode'

const CONTROL_CHARACTERS_REGEX = new RegExp(
  `[${String.fromCharCode(0)}-${String.fromCharCode(31)}\u007F]`,
  'g',
)
const DANGEROUS_PROTOCOL_REGEX = /(javascript:|data:text\/html)/gi
const TAG_LIKE_REGEX = /<\/?\s*script.*?>/gi
const WHITESPACE_CLEANUP_REGEX = /\s{2,}/g

const PURIFY_OPTIONS = {
  ALLOWED_TAGS: [],
  ALLOWED_ATTR: {},
}

const SANITIZE_HTML_OPTIONS = {
  allowedTags: [],
  allowedAttributes: {},
}

const fallbackSanitize = (value = '') =>
  String(value ?? '')
    .replace(CONTROL_CHARACTERS_REGEX, '')
    .replace(DANGEROUS_PROTOCOL_REGEX, '')
    .replace(TAG_LIKE_REGEX, '')
    .replace(/[<>]/g, '')
    .replace(WHITESPACE_CLEANUP_REGEX, ' ')
    .trim()

const applyDomPurify = (value = '', options = PURIFY_OPTIONS) => {
  if (DOMPurify?.sanitize) {
    return DOMPurify.sanitize(value, options)
  }
  return fallbackSanitize(value)
}

const applySanitizeHtml = (value = '', options = SANITIZE_HTML_OPTIONS) => {
  if (typeof sanitizeHtml === 'function') {
    return sanitizeHtml(value, options)
  }
  return fallbackSanitize(value)
}

const sanitizeWithLibraries = (value = '') => {
  const purified = applyDomPurify(value)
  return applySanitizeHtml(purified)
}

const sanitizePrimitive = (value) => {
  if (typeof value === 'string') {
    return sanitizeWithLibraries(value)
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return value
  }
  if (value instanceof Date) {
    return new Date(Number(value))
  }
  return fallbackSanitize(String(value ?? ''))
}

export function sanitizeInput(value = '') {
  return sanitizePrimitive(value)
}

export function sanitizeRichText(value = '', options = {}) {
  const mergedPurifyOptions = {
    ...PURIFY_OPTIONS,
    ALLOWED_TAGS: options.allowedTags ?? PURIFY_OPTIONS.ALLOWED_TAGS,
    ALLOWED_ATTR: options.allowedAttributes ?? PURIFY_OPTIONS.ALLOWED_ATTR,
  }
  const mergedHtmlOptions = {
    ...SANITIZE_HTML_OPTIONS,
    allowedTags: options.allowedTags ?? SANITIZE_HTML_OPTIONS.allowedTags,
    allowedAttributes: options.allowedAttributes ?? SANITIZE_HTML_OPTIONS.allowedAttributes,
  }
  const purified = applyDomPurify(value, mergedPurifyOptions)
  return applySanitizeHtml(purified, mergedHtmlOptions)
}

export function sanitizeRecord(record) {
  if (Array.isArray(record)) {
    return record.map((item) => sanitizeRecord(item))
  }
  if (record && typeof record === 'object') {
    return Object.entries(record).reduce((accumulator, [key, value]) => {
      const sanitizedKey = sanitizePrimitive(key)
      accumulator[sanitizedKey] = sanitizeRecord(value)
      return accumulator
    }, {})
  }
  return sanitizePrimitive(record)
}

export function isValidEmail(value = '') {
  const normalized = sanitizePrimitive(value)
  return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(normalized)
}

export function decodeJwtPayload(token = '') {
  try {
    return sanitizeRecord(jwtDecode(token))
  } catch (error) {
    console.warn('[security] Failed to decode JWT:', error)
    return null
  }
}

export function extractTokenExpiry(payload) {
  if (!payload) {
    return null
  }
  const expiry = Number(payload.exp ?? payload.expiresAt ?? 0)
  if (!Number.isFinite(expiry) || expiry <= 0) {
    return null
  }
  return expiry > 10_000 ? expiry * 1000 : expiry
}

export function redactSecretsFromObject(record, redactedValue = '***') {
  const sensitiveKeys = ['token', 'accessToken', 'refreshToken', 'password', 'secret']
  if (!record || typeof record !== 'object') {
    return record
  }
  return Object.entries(record).reduce((accumulator, [key, value]) => {
    if (sensitiveKeys.some((sensitiveKey) => key.toLowerCase().includes(sensitiveKey.toLowerCase()))) {
      accumulator[key] = redactedValue
      return accumulator
    }
    accumulator[key] = sanitizeRecord(value)
    return accumulator
  }, {})
}
